import { Check } from "lucide-react";
import PhoneFrame from "@/components/marketing/phoneFrame/phoneFrame.component";
import type { ReactNode } from "react";

export type GameScreenState = "wait" | "offer" | "done";

export interface GameScreenProps {
  shop: string;
  game: string;
  state: GameScreenState;
  wait: { title: string; text: string };
  offer: { label: string; title: string; text: string; action: ReactNode };
  done: { title: string; text: string; action?: ReactNode };
}

const panelClass =
  "grid flex-1 content-center justify-items-center gap-2 p-[22px] text-center";

// The customer's phone next to a game demo, drawn in the shared PhoneFrame
// (red status bar running into the red shop header), then one of three
// panels - waiting for the game, the offer won, or the redeemed
// confirmation. The body is a polite live region so each change is read out.
const GameScreen = ({
  shop,
  game,
  state,
  wait,
  offer,
  done,
}: GameScreenProps) => (
  <PhoneFrame statusBar="brand" contentClassName="flex flex-col pb-6">
    <div className="grid gap-px bg-primary px-[18px] pt-1 pb-3 text-white">
      <span className="font-bold">{shop}</span>
      <span className="text-sm text-white/85">{game}</span>
    </div>
    <div aria-live="polite" className="flex flex-1 flex-col">
      {state === "wait" && (
        <div className={panelClass}>
          <span className="text-[1.2rem] font-bold">{wait.title}</span>
          <p className="min-h-[2.6em] font-bold text-ink-muted">{wait.text}</p>
        </div>
      )}
      {state === "offer" && (
        <div
          className={`${panelClass} bg-linear-to-b from-paper to-white motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-90 motion-safe:duration-500`}
        >
          <span className="text-xs font-extrabold tracking-[0.08em] text-primary uppercase">
            {offer.label}
          </span>
          <span className="text-[1.9rem] leading-[1.1] font-bold tracking-[-0.5px]">
            {offer.title}
          </span>
          <p className="mb-2 text-[14.5px] text-ink-muted">{offer.text}</p>
          {offer.action}
        </div>
      )}
      {state === "done" && (
        <div className={`${panelClass} bg-tone-mint text-tone-mint-ink`}>
          <span className="grid size-[54px] place-items-center rounded-full bg-tone-mint-ink text-white">
            <Check className="size-7" strokeWidth={3} aria-hidden="true" />
          </span>
          <span className="text-[1.2rem] font-bold">{done.title}</span>
          <p className="mb-2 font-bold">{done.text}</p>
          {done.action}
        </div>
      )}
    </div>
  </PhoneFrame>
);

export default GameScreen;
